import { useMemo, useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { useTranslation } from "@/core/i18n";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface SkinBalanceItem {
  id: string;
  label: string;
}

interface SkinBalanceProps {
  options?: SkinBalanceItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

export const SkinBalance = ({
  options,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText,
  skipText,
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: SkinBalanceProps) => {
  const { t } = useTranslation();
  const defaultSkinBalanceOptions = useMemo<SkinBalanceItem[]>(
    () => [
      { id: "very-dry", label: t("skinBalance.optionVeryDry") },
      { id: "mostly-dry", label: t("skinBalance.optionMostlyDry") },
      { id: "combination", label: t("skinBalance.optionCombination") },
      { id: "mostly-oily", label: t("skinBalance.optionMostlyOily") },
      { id: "balanced", label: t("skinBalance.optionBalanced") },
    ],
    [t],
  );
  const resolvedOptions = options ?? defaultSkinBalanceOptions;
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const resolvedSkipText = skipText ?? t("skinBalance.skipForNow");
  const [selected, setSelected] = useState<SkinBalanceItem | null>(
    initialSelectedId ? (resolvedOptions.find((o) => o.id === initialSelectedId) ?? null) : null,
  );

  const handleSelectionChange = (item: SkinBalanceItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {resolvedContinueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {resolvedSkipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">{t("skinBalance.heading")}</PersonalisationHeading>

      <SingleChoiceTagPicker
        items={resolvedOptions}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel={t("skinBalance.ariaLabel")}
      />
    </PersonalisationScreenLayout>
  );
};
